import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { useLazyQuery } from '@apollo/client';
import { Alert, Button, Column, Container, Row } from '@pella/sash-framework';
import { Image, LoadingOverlay } from '../../../';
import { contentstackClient } from '../../../../utils/contentstack-client';
import { GET_ARTICLES_BY_TAXONOMY } from '../../../../enums/contentstack-graphql';
import { isValidArray } from '../../../utilities/validations';

import ProjectFilters from '../../../template/category-inspiration/filters';

const ArticleGrid = ({ data, productType, caseStudyType, staticTiles, baseFilters }) => {
	const [articleData, setArticleData] = useState({ entries: staticTiles?.nodes || [], totalCount: staticTiles?.totalCount });
	const [activeFilters, setActiveFilters] = useState({ base: baseFilters?.[0] });
	const [skip, setSkip] = useState(0);
	const [loading, setLoading] = useState(false);
	const [getArticles] = useLazyQuery(GET_ARTICLES_BY_TAXONOMY, { client: contentstackClient, fetchPolicy: 'network-only', onCompleted: (entries) => onLoadComplete(entries?.all_articles) });

	useEffect(() => {
		getArticles({ variables: { limit: 16, skip, filters: restructureFilters(activeFilters) } });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
	}, []);

	const onLoadComplete = (data) => {
		if (!isValidArray(data.items)) {
			setArticleData({ total: 0, entries: [] });
			setSkip(0);
			setLoading(false);

			return;
		}

		let newArr;

		if (skip > 0) {
			newArr = [...articleData.entries, ...data.items];
		} else {
			newArr = data.items;
		}

		setArticleData({ totalCount: data.total, entries: newArr });
		setSkip(skip + data?.items?.length);
		setLoading(false);
	};

	const handleLoadMoreClick = (event) => {
		event.preventDefault();
		setLoading(true);

		getArticles({ variables: { limit: 16, skip, filters: restructureFilters(activeFilters) } });
	};

	const onSelectChange = (event) => {
		setLoading(true);

		let newFilters = { ...activeFilters };
		newFilters[event.target.name] = event.target.value;

		getArticles({ variables: { filters: restructureFilters(newFilters), limit: 16, skip: 0 } });

		setActiveFilters(newFilters);
		setSkip(0);
	};

	const restructureFilters = (filters) =>
		Object.values(filters)
			.filter((value) => Boolean(value))
			.flat()
			.map((value) => ({
				taxonomies: {
					filter_by: {
						term_in: [...value?.split(', ')],
					},
				},
			}));

	return (
		<div
			className='article-grid py-7 md:py-10 xl:py-[52px]'
			data-testid='article-grid'
		>
			<Container>
				{data?.heading && <h2 className='text-h3 mb-6'>{data.heading}</h2>}

				{loading && <LoadingOverlay />}

				<ProjectFilters
					isPro
					onSelectChange={onSelectChange}
					activeFilters={activeFilters}
					caseStudyType={caseStudyType}
					productType={productType}
				/>

				{!isValidArray(articleData.entries) && (
					<Alert
						type='info'
						hideClose
						className='mb-6'
					>
						No entries were found.
					</Alert>
				)}

				{isValidArray(articleData.entries) && (
					<Row className='pb-8'>
						{articleData.entries.map((article, index) => {
							const thumbnailImage = article?.seo?.thumbnailConnection?.edges?.[0]?.node || article?.seo?.thumbnail;

							return (
								<Column
									xs={12}
									sm={6}
									md={4}
									xl={3}
									key={index}
								>
									<Link
										to={article.url}
										className='group block decoration-transparent'
									>
										{thumbnailImage?.url && (
											<div className='relative aspect-[1.6] overflow-hidden'>
												<Image
													data={{ image: thumbnailImage }}
													params='?format=pjpg&auto=webp&height=300&quality=90'
													className='!block h-full w-full !object-cover transition-transform duration-300 group-focus-within:scale-110 group-hover:scale-110'
												/>
											</div>
										)}

										{article.seo?.title && <p className='inspireCard-heading text-subtitle mb-2.5 mt-3 font-bold'>{article.seo.title}</p>}

										{article.seo?.description && (
											<div
												className='inspireCard-desc mb-9 [&_a]:hidden'
												dangerouslySetInnerHTML={{ __html: article.seo.description }}
											/>
										)}
									</Link>
								</Column>
							);
						})}
					</Row>
				)}

				{articleData.totalCount > articleData.entries.length && (
					<div className='mb-9 pb-9 text-center'>
						<Button onClick={handleLoadMoreClick}>Show More Results</Button>
					</div>
				)}
			</Container>
		</div>
	);
};

ArticleGrid.propTypes = {
	data: PropTypes.object,
	caseStudyType: PropTypes.string,
	staticTiles: PropTypes.object,
	baseFilters: PropTypes.array,
	productType: PropTypes.string,
};

export default ArticleGrid;
